import React, { useState, useMemo } from 'react';
import { Printer } from 'lucide-react';
import FormattedCurrency from '../common/FormattedCurrency';
import FullBlockingModal from '../common/FullBlockingModal';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '../common/Button';
import CollapsibleContent from '../common/CollapsibleContent';
import { Quote, ChildQuote, QuoteJob } from '../../types/quote';
import { computeQuoteTotalAmount } from '../../utils/jobUtils';
import { Job, JobQuote } from '../../types/job';
import { useHasPermission } from '../../hooks/useHasPermission';
import { canRecruiterRespondToQuote } from '../../utils/quoteUtils';
import { RequestChangeQuoteModal } from './RequestChangeQuoteModal';
import { useQuoteStore } from '../../stores/quoteStore';
import { useJobStore } from '../../stores/jobStore';
import { QuoteDetailsInfo } from './QuoteDetailsInfo';
import { useNavigate } from 'react-router-dom';

const SuccessModal = ({
  title,
  message,
  isAcceptingQuote = false,
  onClose,
}: {
  title: string;
  message: React.ReactNode;
  isAcceptingQuote?: boolean;
  onClose: () => void;
}) => {
  const { t } = useTranslation(['jobs']);
  const btnLabel = isAcceptingQuote
    ? t('viewProject', { ns: 'jobs' })
    : t('close_and_back_to_my_offers', { ns: 'jobs' });
  return (
    <FullBlockingModal innerClassName="md:max-w-[48rem]">
      <h3 className="text-2xl font-bold text-secondary mb-4">{title}</h3>
      <p className="text-secondary mb-4">{message}</p>
      <div className="flex flex-col gap-4 md:flex-row-reverse md:items-center">
        <Button
          type="button"
          onClick={onClose}
          className="w-full md:w-auto md:px-8"
          variant="primary"
        >
          {btnLabel}
        </Button>
      </div>
    </FullBlockingModal>
  );
};

export default function BidderDetailsModal({
  onClose,
  job,
  quote,
}: {
  onClose: () => void;
  job: QuoteJob | Job;
  quote: Quote | ChildQuote | JobQuote;
}) {
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [showAcceptSuccessModal, setShowAcceptSuccessModal] = useState(false);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showRejectSuccessModal, setShowRejectSuccessModal] = useState(false);
  const [rejectMessage, setRejectMessage] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);

  const [showRequestChangeModal, setShowRequestChangeModal] = useState(false);
  const navigate = useNavigate();

  const { t } = useTranslation(['quotes', 'freelancers', 'common', 'jobs']);

  const hasAcceptQuotePermission = useHasPermission('accept_quote');
  const hasDeclineQuotePermission = useHasPermission('decline_quote');
  const hasRequestChangesPermission = useHasPermission('request_changes');

  const { acceptQuote, rejectQuote } = useQuoteStore();
  const { fetchJobs } = useJobStore();

  const freshQuote = useQuoteStore(s => s.quotes.find(q => String(q.id) === String(quote.id)));
  const displayQuote = freshQuote ?? quote;
  const hasChangeRequested =
    'change_request_message' in displayQuote && !!displayQuote.change_request_message?.trim();

  const freelancerNames = useMemo((): string => {
    const names: string[] = [];

    if ('quote_owner' in displayQuote) {
      names.push(displayQuote.quote_owner.first_name);
    }
    if ('alliances' in displayQuote) {
      const teamMembers = displayQuote.alliances
        .filter(alliance => alliance.child_quote)
        .map(alliance => alliance.child_quote!.quote_owner.first_name);
      names.push(...teamMembers);
    }

    if (names.length === 0) return '';
    if (names.length === 1) return names[0];
    if (names.length === 2) return names.join(' et ');
    return names.slice(0, -1).join(', ') + ' et ' + names[names.length - 1];
  }, [displayQuote]);

  const handleAcceptQuote = async () => {
    try {
      setAcceptError(null);
      setAccepting(true);
      await acceptQuote(quote.id);
      setShowAcceptSuccessModal(true);
    } catch (error) {
      console.error('Error accepting quote:', error);
      setAcceptError(t('error.generic', { ns: 'common' }));
    } finally {
      setAccepting(false);
    }
  };

  const handleRejectQuote = async () => {
    try {
      setRejecting(true);
      setRejectError(null);
      await rejectQuote(quote.id, rejectMessage);
      setShowRejectModal(false);
      setShowRejectSuccessModal(true);
    } catch (error) {
      console.error('Error rejecting quote:', error);
      setRejectError(t('error.generic', { ns: 'common' }));
    } finally {
      setRejecting(false);
    }
  };

  const teamMembers =
    'alliances' in displayQuote
      ? displayQuote.alliances
          .filter(alliance => alliance.child_quote)
          .map(alliance => alliance.child_quote!)
      : [];

  const totalAmount = computeQuoteTotalAmount(displayQuote);
  const isQuoteRespondable = canRecruiterRespondToQuote(displayQuote, job.status);
  const showAdjustmentBtn =
    Boolean(totalAmount) && hasRequestChangesPermission && isQuoteRespondable;

  const hasTeamMembers = teamMembers.length > 0;

  return (
    <>
      <FullBlockingModal innerClassName="md:max-w-[70rem]">
        <button
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl md:top-0"
          onClick={onClose}
          aria-label={t('common:form_actions.close')}
        >
          &times;
        </button>

        <h2 className="text-secondary text-2xl font-bold">
          {t('bidders', { ns: 'jobs', defaultValue: 'Pigistes soumissionnaires' })}
        </h2>
        <p className="text-lg font-bold text-secondary mt-3">{job.title}</p>

        <hr className="border-[#E1E9F0] my-3" />

        <CollapsibleContent
          className="bg-white flex flex-col transition-all duration-300"
          header={
            <QuoteDetailsInfo
              quote={displayQuote}
              job={job}
              variant="offer"
              headerOnly={true}
              avatarClassName="self-center"
            />
          }
          defaultExpanded={!hasTeamMembers}
          contentClassName="bg-white"
          headerClassName="relative items-center"
          expandButtonClassName="absolute md:static self-start md:self-center"
        >
          <QuoteDetailsInfo
            quote={displayQuote}
            job={job}
            variant="offer"
            detailsOnly={true}
            title={t('quotes', { ns: 'quotes' })}
            subTotalLabel={
              hasTeamMembers
                ? t('sub_total', { ns: 'quotes' })
                : t('createQuoteModal.totalAmountSectionTitle', { ns: 'quotes' })
            }
          />
        </CollapsibleContent>

        {hasTeamMembers && (
          <div className="mt-4">
            <h4 className="font-bold text-sm uppercase md:text-xs text-secondary">
              {t('teamMembers', { ns: 'quotes' })}
            </h4>
            {teamMembers.map((childQuote, idx) => (
              <div key={idx} className="border-b border-[#E1E9F0] py-4">
                <CollapsibleContent
                  className="bg-white flex flex-col transition-all duration-300"
                  header={
                    <QuoteDetailsInfo
                      quote={childQuote}
                      job={job}
                      headerOnly={true}
                      variant="offer"
                      avatarClassName="md:w-12 md:h-12 self-center"
                    />
                  }
                  contentClassName="bg-white"
                  headerClassName="relative items-center"
                  expandButtonClassName="absolute md:static self-start md:self-center"
                  defaultExpanded={false}
                >
                  <QuoteDetailsInfo
                    quote={childQuote}
                    job={job}
                    detailsOnly={true}
                    variant="offer"
                    title={t('quotes', { ns: 'quotes' })}
                  />
                </CollapsibleContent>
              </div>
            ))}
          </div>
        )}

        {totalAmount > 0 && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto] items-start md:items-center gap-4 md:gap-6">
            <div className={showAdjustmentBtn ? '' : 'md:col-span-2'}>
              <h4 className="text-secondary font-bold text-lg">
                {t('createQuoteModal.totalAmountSectionTitle', { ns: 'quotes' })}
              </h4>
              <span className="text-xl text-primary font-bold">
                <FormattedCurrency value={totalAmount} />
              </span>
            </div>
            {showAdjustmentBtn &&
              (hasChangeRequested ? (
                <Button
                  variant="transparent-with-border"
                  className="w-full md:w-auto md:px-8 md:justify-self-end"
                  disabled
                >
                  {t('change_has_been_requested', { ns: 'quotes' })}
                </Button>
              ) : (
                <Button
                  variant="transparent-with-border"
                  className="w-full md:w-auto md:px-8 md:justify-self-end"
                  onClick={() => setShowRequestChangeModal(true)}
                >
                  {t('request_changes', { ns: 'quotes' })}
                </Button>
              ))}
          </div>
        )}

        {acceptError && <div className="text-[#F04438] font-ambit mt-4">{acceptError}</div>}

        <div className="flex flex-col flex-wrap gap-4 mt-8 md:flex-row-reverse md:items-center md:mt-4">
          {hasAcceptQuotePermission && isQuoteRespondable && (
            <Button
              type="button"
              onClick={handleAcceptQuote}
              className="w-full md:w-auto md:px-8"
              variant="primary"
              disabled={accepting}
            >
              {accepting ? t('common:form_actions.accepting') : t('acceptQuote', { ns: 'quotes' })}
            </Button>
          )}

          {hasDeclineQuotePermission && isQuoteRespondable && (
            <Button
              type="button"
              onClick={() => setShowRejectModal(true)}
              className="w-full md:w-auto md:px-8"
              variant="transparent-with-border"
            >
              {t('declineQuote', { ns: 'quotes' })}
            </Button>
          )}

          <Button
            type="button"
            onClick={() =>
              window.open(
                `/quotes/${quote.id}/print?jobId=${encodeURIComponent(job.id)}`,
                '_blank',
                'noopener'
              )
            }
            variant="transparent-with-border"
            className="w-full md:w-auto md:px-8 inline-flex items-center gap-2"
          >
            <Printer size={18} aria-hidden />
            <span>{t('print.printButton', { ns: 'quotes', defaultValue: 'Imprimer' })}</span>
          </Button>

          <Button
            type="button"
            onClick={onClose}
            variant="simple-text"
            className="w-full md:w-auto md:px-8"
          >
            {t('common:form_actions.close', {
              ns: 'common',
              defaultValue: 'Fermer',
            })}
          </Button>
        </div>

        {showRequestChangeModal && (
          <RequestChangeQuoteModal
            freelancerQuote={displayQuote}
            parentQuote={displayQuote}
            onClose={() => setShowRequestChangeModal(false)}
          />
        )}

        {showRejectModal && (
          <FullBlockingModal innerClassName="md:max-w-[68rem]">
            <button
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl"
              onClick={() => setShowRejectModal(false)}
              aria-label={t('common:form_actions.close')}
            >
              &times;
            </button>
            <div className="flex flex-col gap-4">
              <h3 className="text-2xl font-bold text-secondary">
                {t('rejectQuote.title', { ns: 'jobs' })}
              </h3>
              <p className="text-secondary">{t('rejectQuote.message', { ns: 'jobs' })}</p>
              <div className="mb-4">
                <label className="text-secondary mb-2 block" htmlFor="declineReason">
                  <Trans
                    i18nKey={'rejectQuote.label'}
                    ns={'jobs'}
                    values={{ name: freelancerNames }}
                    components={{ bold: <span className="font-bold" /> }}
                  />
                </label>
                <textarea
                  id="declineReason"
                  value={rejectMessage}
                  onChange={e => setRejectMessage(e.target.value)}
                  placeholder={t('rejectQuote.placeholder', { ns: 'jobs' })}
                  className="w-full h-24 border-0 px-3 py-2 font-ambit text-[0.8125rem] not-italic font-normal leading-[1.25rem] bg-[#E1E9F0]"
                  maxLength={1000}
                />
                {rejectError && <div className="text-[#F04438] font-ambit mt-1">{rejectError}</div>}
              </div>

              <div className="flex flex-col gap-4 md:flex-row-reverse md:items-center">
                <Button
                  type="button"
                  onClick={handleRejectQuote}
                  className="flex-1"
                  variant="primary"
                  disabled={rejecting || !rejectMessage.trim()}
                >
                  {rejecting
                    ? t('common:form_actions.sending')
                    : t('rejectQuote.send', { ns: 'jobs' })}
                </Button>
                <Button
                  type="button"
                  onClick={() => setShowRejectModal(false)}
                  className="flex-1"
                  variant="simple-text"
                >
                  {t('rejectQuote.cancel', { ns: 'jobs' })}
                </Button>
              </div>
            </div>
          </FullBlockingModal>
        )}

        {showAcceptSuccessModal && (
          <SuccessModal
            title={t('quoteAccepted.title', { ns: 'jobs' })}
            isAcceptingQuote={true}
            message={
              <Trans
                i18nKey={'quoteAccepted.message'}
                ns={'jobs'}
                values={{ projectName: job.title, freelancers: freelancerNames }}
                components={{ bold: <span className="font-bold" /> }}
              />
            }
            onClose={() => {
              fetchJobs();
              onClose();
              navigate('/projects', { state: { selected_id: quote.id } });
            }}
          />
        )}

        {showRejectSuccessModal && (
          <SuccessModal
            title={t('rejectQuote.success.title', { ns: 'jobs' })}
            message={
              <Trans
                i18nKey={'rejectQuote.success.message'}
                ns={'jobs'}
                values={{ freelancers: freelancerNames }}
                components={{ bold: <span className="font-bold" /> }}
              />
            }
            onClose={() => {
              fetchJobs();
              setShowRejectSuccessModal(false);
              onClose();
            }}
          />
        )}
      </FullBlockingModal>
    </>
  );
}
